import { AppButton, Input, Radio, Tooltip } from '@clickguard/design-system'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { useStores } from 'hooks'
import { FormEvent, useState } from 'react'
import { useUiStore } from 'store'
import { addMemberSchema } from './validations'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { useTrackPostHogEvent } from '../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../utils/posthog-events'

export const AddMember = () => {
	const { accountStore, userStore } = useStores()
	const { user } = userStore
	const setModalContent = useUiStore((s) => s.setModalContent)
	const { userAccessLevel, wantsToSeeDemoData } = accountStore

	const renderModal = () => {
		setModalContent({
			title: 'Add Member',
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			body: <AddMemberModalBody />
		})
	}

	const getDisabledTooltip = () => {
		if (wantsToSeeDemoData)
			return 'This action is disabled when demo data is displayed'
		if (userAccessLevel(user!.id) === 'READ')
			return 'Analysts don’t have permission for this action'
		if (userAccessLevel(user!.id) === 'WRITE')
			return 'Manager don’t have permission for this action'
		if (!accountStore.canAddTeamMember()) {
			return accountStore.isPreTrial()
				? 'Start your trial to add more team members'
				: 'Upgrade your subscription to add more team members'
		}
		return undefined
	}

	const disabledTooltip = getDisabledTooltip()
	const button = (
		<AppButton
			variant="primary"
			size="md"
			label="Add new member"
			disabled={
				wantsToSeeDemoData ||
				!accountStore.canAddTeamMember() ||
				userAccessLevel(user!.id) === 'READ' ||
				userAccessLevel(user!.id) === 'WRITE'
			}
			onClick={() => renderModal()}
		/>
	)

	return disabledTooltip ? (
		<Tooltip content={disabledTooltip} portal>
			{button}
		</Tooltip>
	) : (
		button
	)
}

export const AddMemberModalBody = () => {
	const {
		accountStore,
		userStore: { user }
	} = useStores()
	const { inviteAccountUser, account, isOwnerAccess } = accountStore
	const setModalState = useUiStore((s) => s.setModalState)
	const trackPostHogEvent = useTrackPostHogEvent()

	const [addMemberForm, setAddMemberForm] = useState<Record<string, string>>({
		email: '',
		role: 'WRITE'
	})
	const [formErrors, setFormErrors] = useState<null | Partial<{
		email: string
		role: string
	}>>()

	const handleChange = ({ currentTarget: input }: any) => {
		const form = { ...addMemberForm }
		form[input.name] = input.value
		setAddMemberForm(form)
	}

	const validate = () => {
		const options = { abortEarly: false }
		const { error } = addMemberSchema.validate(
			{ email: addMemberForm.email, role: addMemberForm.role },
			options
		)
		if (!error) return null
		const errors: Record<string, string> = {}

		error.details.forEach((item) => {
			errors[item.path[0]] = item.message
		})
		return errors
	}

	const confirmSendInvite = async () => {
		const payload = {
			email: addMemberForm.email,
			access_level: addMemberForm.role
		}
		const errors = validate()
		setFormErrors(errors)
		if (errors) return
		try {
			await inviteAccountUser(account!.id, payload)
			trackPostHogEvent(
				PostHogEvent.ADD_MEMBER,
				PostHogPath.SETTINGS_TEAM_ACCESS,
				null
			)
			setModalState(false)
		} catch (e) {
			console.error(e)
		}
	}

	return (
		<>
			<form
				data-hs-do-not-collect="true"
				onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
			>
				<div className="my-4 w-full">
					<Input
						label="Email Address"
						type="email"
						name="email"
						size="md"
						state={formErrors?.email ? 'error' : 'default'}
						placeholder="john.doe@company.com"
						error={formErrors?.email}
						value={addMemberForm.email}
						onChange={handleChange}
					/>
				</div>
				<div className="flex flex-col gap-2 w-full">
					<div className="text-lg font-medium text-t-base-blue">Team role</div>
					<div className="flex items-center gap-1">
						<Radio
							checked={addMemberForm.role === 'WRITE'}
							label="Manager"
							name="role"
							value={'WRITE'}
							onChange={handleChange}
						/>
						<Tooltip content="Has read and write access." position="top">
							<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
						</Tooltip>
					</div>
					<div className="flex items-center gap-1">
						<Radio
							checked={addMemberForm.role === 'READ'}
							label="Analyst"
							name="role"
							value={'READ'}
							onChange={handleChange}
						/>
						<Tooltip content="Has read-only access." position="top">
							<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
						</Tooltip>
					</div>
					{isOwnerAccess(user!.id) && (
						<>
							<div className="text-lg text-t-base-blue font-medium flex items-center gap-1">
								Transfer ownership
								<Tooltip
									content="Transferring ownership to another member will make them a sole workspace owner, and your role will be reduced to manager."
									position="top"
								>
									<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
								</Tooltip>
							</div>
							<Radio
								checked={addMemberForm.role === 'OWNER'}
								label="Owner"
								name="role"
								value={'OWNER'}
								onChange={handleChange}
							/>
						</>
					)}
				</div>
			</form>
			<div className="flex justify-end gap-4 mt-5">
				<AppButton
					variant="primary"
					size="lg"
					fullWidth
					label="Invite"
					onClick={() => confirmSendInvite()}
				/>
			</div>
		</>
	)
}
